// Simplified Signup page for InfluenceHub MVP
// Single-step signup that creates email/password accounts only
// Collects: email, password, confirm password

import React, { useState } from 'react';
import { createUserWithEmailAndPassword } from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import { auth, db } from '../../firebaseConfig.js';
import { normalizeEmail, validateEmail } from '../lib/validation/email';
import AuthLayout from '../components/auth/AuthLayout';
import GoogleAuthButton from '../components/auth/GoogleAuthButton';
import { signInWithGoogle, ensureUserDocFromAuthUser } from '../lib/auth';
import { getPostAuthRouteWithLazyMigration } from '../lib/getPostAuthRoute';
import {
  needsBackfill,
  backfillOnboardingCompleted,
  hasSelectableRoleSet,
} from '../lib/onboardingLegacy';
import { ensureUserDoc } from '../lib/firestore/users';
import { triggerWelcomeEmail } from '../lib/email/triggerWelcomeEmail';

const Signup = ({ onNavigate }) => {
  // Form state
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  
  // UI state
  const [loading, setLoading] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [touched, setTouched] = useState({
    email: false,
    password: false,
    confirmPassword: false,
  });
  const [submitAttempted, setSubmitAttempted] = useState(false);

  // Handle input changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    switch (name) {
      case 'email':
        setEmail(value);
        break;
      case 'password':
        setPassword(value);
        break;
      case 'confirmPassword':
        setConfirmPassword(value);
        break;
    }
    if (submitError) setSubmitError('');
  };

  const handleBlur = (e) => {
    const { name } = e.target;
    setTouched((prev) => ({ ...prev, [name]: true }));
  };

  const getFieldErrors = () => {
    const errors = {};

    const emailValidation = validateEmail(email);
    if (!emailValidation.ok) {
      errors.email = emailValidation.reason || 'Enter a valid email address.';
    }

    if (!password) {
      errors.password = 'Password is required.';
    } else if (password.length < 8) {
      errors.password = 'Password must be at least 8 characters.';
    }

    if (!confirmPassword) {
      errors.confirmPassword = 'Confirm your password.';
    } else if (password && confirmPassword !== password) {
      errors.confirmPassword = 'Passwords do not match.';
    }

    return errors;
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitError('');
    setSubmitAttempted(true);

    const validationErrors = getFieldErrors();
    if (Object.keys(validationErrors).length > 0) {
      return;
    }

    setLoading(true);

    try {
      const normalizedEmail = normalizeEmail(email);
      const userCredential = await createUserWithEmailAndPassword(
        auth,
        normalizedEmail,
        password
      );

      const uid = userCredential.user.uid;
      await ensureUserDoc(uid, userCredential.user.email ?? null);
      console.log('[signup] Firestore user doc created', { uid });
      await triggerWelcomeEmail(uid);
      const userDocSnap = await getDoc(doc(db, 'users', uid));
      const data = userDocSnap.exists() ? userDocSnap.data() : null;
      const route = await getPostAuthRouteWithLazyMigration(uid, data, userCredential.user);
      onNavigate(route, null, { replace: true });

    } catch (err) {
      console.error('Signup error:', err);
      setLoading(false);
      
      // Handle Firebase Auth errors
      switch (err.code) {
        case 'auth/email-already-in-use':
          setSubmitError('An account with this email already exists.');
          break;
        case 'auth/invalid-email':
          setSubmitError('Invalid email address.');
          break;
        case 'auth/weak-password':
          setSubmitError('Password is too weak. Please choose a stronger password.');
          break;
        case 'auth/too-many-requests':
          setSubmitError('Too many attempts. Please try again later.');
          break;
        default:
          setSubmitError('We couldn\'t create your account. Please try again.');
      }
    }
  };

  const fieldErrors = getFieldErrors();
  const isFormValid = Object.keys(fieldErrors).length === 0;

  return (
    <AuthLayout
      title="Join InfluenceHub"
      subtitle="Create your account to start building your professional creator profile."
      cardClassName="px-8 py-4"
      onHomeNavigate={onNavigate ? () => onNavigate('/') : undefined}
    >
      <div className="mb-6">
        <GoogleAuthButton 
          text="Sign up with Google" 
          onClick={async () => {
            try {
              setLoading(true);
              setSubmitError('');
              const user = await signInWithGoogle();
              const userDocSnap = await ensureUserDocFromAuthUser(user);
              console.log('[signup] Firestore user doc ensured (Google)', { uid: user.uid });
              await triggerWelcomeEmail(user.uid);
              let data = userDocSnap.exists() ? userDocSnap.data() : null;
              if (needsBackfill(user, data)) {
                await backfillOnboardingCompleted(user.uid);
                const refetch = await getDoc(doc(db, 'users', user.uid));
                data = refetch.exists() ? refetch.data() : null;
              }
              const postAuthRoute = hasSelectableRoleSet(data)
                ? await getPostAuthRouteWithLazyMigration(user.uid, data, user)
                : '/select-role';
              onNavigate(postAuthRoute, null, { replace: true });
            } catch (err) {
              setSubmitError(err.message || 'Failed to sign up with Google');
              console.error('Google auth error:', err);
              setLoading(false);
            }
          }}
        />
        
        <div className="relative mt-6">
          <div className="absolute inset-0 flex items-center" aria-hidden="true">
            <div className="w-full border-t border-slate-200"></div>
          </div>
          <div className="relative flex justify-center text-sm font-medium leading-6">
            <span className="bg-white px-4 text-slate-400">or</span>
          </div>
        </div>
      </div>

      <form className="space-y-3" onSubmit={handleSubmit}>
        {/* Email Input */}
        <div>
          <label htmlFor="email" className="text-sm font-medium text-slate-700">
            Email Address
          </label>
          <input
            id="email"
            name="email"
            type="email"
            required
            value={email}
            onChange={handleChange}
            onBlur={handleBlur}
            className="mt-1 h-11 w-full rounded-md border border-slate-300 px-3 text-sm text-slate-900 placeholder:text-slate-400 focus:border-[#1B9DA2] focus:outline-none focus:ring-1 focus:ring-[#1B9DA2]"
            placeholder="you@example.com"
            autoComplete="email"
          />
          <div className="mt-1 min-h-[18px] text-sm text-red-500">
            {(touched.email || submitAttempted) && fieldErrors.email ? fieldErrors.email : ''}
          </div>
        </div>

        {/* Password Input */}
        <div>
          <label htmlFor="password" className="text-sm font-medium text-slate-700">
            Password
          </label>
          <input
            id="password"
            name="password"
            type="password"
            required
            value={password}
            onChange={handleChange}
            onBlur={handleBlur}
            className="mt-1 h-11 w-full rounded-md border border-slate-300 px-3 text-sm text-slate-900 placeholder:text-slate-400 focus:border-[#1B9DA2] focus:outline-none focus:ring-1 focus:ring-[#1B9DA2]"
            placeholder="Create a password"
            autoComplete="new-password"
          />
          <p className="mt-1 text-sm text-slate-500">
            Must be at least 8 characters
          </p>
          <div className="mt-1 min-h-[16px] text-sm text-red-500">
            {(touched.password || submitAttempted) && fieldErrors.password ? fieldErrors.password : ''}
          </div>
        </div>

        {/* Confirm Password Input */}
        <div>
          <label htmlFor="confirmPassword" className="text-sm font-medium text-slate-700">
            Confirm Password
          </label>
          <input
            id="confirmPassword"
            name="confirmPassword"
            type="password"
            required
            value={confirmPassword}
            onChange={handleChange}
            onBlur={handleBlur}
            className="mt-1 h-11 w-full rounded-md border border-slate-300 px-3 text-sm text-slate-900 placeholder:text-slate-400 focus:border-[#1B9DA2] focus:outline-none focus:ring-1 focus:ring-[#1B9DA2]"
            placeholder="Confirm your password"
            autoComplete="new-password"
          />
          <div className="mt-1 min-h-[16px] text-sm text-red-500">
            {(touched.confirmPassword || submitAttempted) && fieldErrors.confirmPassword ? fieldErrors.confirmPassword : ''}
          </div>
        </div>

        <div className="!mt-[10px]">
          {submitError ? (
            <div className="mb-3 rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-600">
              {submitError}
            </div>
          ) : null}
          <button
            type="submit"
            disabled={!isFormValid || loading}
            className="h-12 w-full rounded-lg bg-[#1B9DA2] text-sm font-medium text-white transition hover:bg-[#168589] disabled:cursor-not-allowed disabled:opacity-50"
          >
            {loading ? 'Creating Account...' : 'Create Account'}
          </button>
        </div>

        <div className="text-center">
          <p className="text-sm text-slate-500">
            Already have an account?{' '}
            <button
              type="button"
              onClick={() => onNavigate('/login', null, { replace: true })}
              className="font-medium text-slate-500 transition hover:text-[#7842D6]"
            >
              Sign in
            </button>
          </p>
        </div>
      </form>
    </AuthLayout>
  );
};

export default Signup;
